import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { ShieldCheck } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [mfaToken, setMfaToken] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, loginMfaVerify } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const result = await login(email, password);
      if (result.mfaRequired) { setMfaToken(result.mfaToken); setLoading(false); return; }
      navigate(result.user?.role === "client" ? `/client/${result.user.id}` : "/");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Login failed");
      setLoading(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await loginMfaVerify(mfaToken, code);
      navigate("/admin");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Incorrect code");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden md:flex md:w-1/2 cork-board relative items-center justify-center p-12">
        <div className="paper-card bg-white p-10 rounded-xl -rotate-2 max-w-sm relative">
          <div className="push-pin" style={{"--pin":"#2E6B57"}}/>
          <h2 className="text-4xl font-bold mb-3" style={{fontFamily:'Outfit'}}>Welcome back.</h2>
          <p className="handwritten text-2xl text-[#C79B6B]">Your neighbors missed you.</p>
        </div>
      </div>
      <div className="w-full md:w-1/2 flex items-center justify-center p-8 bg-[#F2F4F2]">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center mb-10" data-testid="login-logo-link">
            <img src="/logo-full.png" alt="LIMYA" className="h-9 w-auto"/>
          </Link>

          {mfaToken ? (
            <>
              <h1 className="text-3xl font-bold mb-2 flex items-center gap-2" style={{fontFamily:'Outfit'}}><ShieldCheck size={24} className="text-[#245544]"/> Enter your code</h1>
              <p className="text-sm text-[#222]/60 mb-8">Open your authenticator app and enter the 6-digit code.</p>
              <form onSubmit={submitMfa} className="space-y-4">
                <input type="text" required autoFocus maxLength={6} value={code} onChange={e=>setCode(e.target.value.replace(/\D/g,""))}
                  className="w-full mt-1 px-4 py-3 bg-white border border-black/10 rounded-xl outline-none focus:ring-2 focus:ring-[#2E6B57] text-center text-2xl tracking-[0.3em] font-mono"
                  placeholder="000000" data-testid="mfa-code-input"/>
                {err && <p className="text-sm text-red-600" data-testid="mfa-error">{err}</p>}
                <button type="submit" disabled={loading || code.length !== 6} className="btn-lime w-full py-3.5 disabled:opacity-60" data-testid="mfa-submit-btn">
                  {loading ? "Verifying…" : "Verify & sign in"}
                </button>
              </form>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-bold mb-2" style={{fontFamily:'Outfit'}}>Sign in</h1>
              <p className="text-sm text-[#222]/60 mb-8">Browse, save, and message local pros.</p>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-[#222]/60">Email</label>
                  <input type="email" required value={email} onChange={e=>setEmail(e.target.value)}
                    className="w-full mt-1 px-4 py-3 bg-white border border-black/10 rounded-xl outline-none focus:ring-2 focus:ring-[#2E6B57] focus:border-transparent"
                    data-testid="login-email-input"/>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-[#222]/60">Password</label>
                  <input type="password" required value={password} onChange={e=>setPassword(e.target.value)}
                    className="w-full mt-1 px-4 py-3 bg-white border border-black/10 rounded-xl outline-none focus:ring-2 focus:ring-[#2E6B57] focus:border-transparent"
                    data-testid="login-password-input"/>
                </div>
                {err && <p className="text-sm text-red-600" data-testid="login-error">{err}</p>}
                <button type="submit" disabled={loading} className="btn-lime w-full py-3.5 disabled:opacity-60" data-testid="login-submit-btn">
                  {loading ? "Signing in…" : "Sign in"}
                </button>
              </form>

              <div className="mt-4 text-center">
                <Link to="/forgot-password" className="text-sm text-[#222]/60 hover:text-[#245544]" data-testid="login-forgot-link">Forgot password?</Link>
              </div>

              <p className="mt-6 text-sm text-[#222]/60 text-center">
                New to LIMYA? <Link to="/register" className="text-[#245544] font-semibold" data-testid="login-register-link">Create an account</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
